import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import { ADMIN_INVOICES_SLICE_NAME } from "./adminInvoices.constants"
import {
  changeInvoiceAmount,
  confirmInvoice,
  getAdminInvoices,
  getAdminInvoicesExport,
  getAdminInvoicesFilterOptions,
  updateAdminInvoice
} from "./adminInvoices.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "email", label: "User email", active: true },
  { field_id: "full_name", label: "User full name", active: true },
  { field_id: "loyal_billing", label: "Loyal billing", active: true },
  { field_id: "payment_system", label: "Payment system", active: true },
  { field_id: "type", label: "Type", active: true },
  { field_id: "invoice_amount", label: "Invoice amount", active: true },
  { field_id: "invoice_date", label: "Invoice date", active: true },
  { field_id: "invoice_id", label: "Invoice ID", active: true },
  { field_id: "business_account", label: "Business account", active: false },
  { field_id: "alerts_period", label: "Alerts period", active: false }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    status: "all",

    full_name: [],
    email: [],
    business_account: [],

    invoice_amount: { min: null, max: null, type: null },

    alerts_period: null,
    invoice_date: null,
    admin_invoice_id: [],
    is_reissued: "all",
    exclude_fields: []
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    country: [],
    bank_name: [],
    connected_payment_processors: []
  },

  visibleFiltersAdmin: { status: true, email: true, loyal_billing: true, payment_system: true },

  exportFetching: false,
  exportResult: null
}

const applyInvoiceUpdate = (state, newInvoice) => {
  state.result.results = state.result.results.map((el) =>
    el.id === newInvoice.id ? newInvoice : el
  )
}

const slice = createSlice({
  name: ADMIN_INVOICES_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `adminInvoicesSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `adminInvoicesSetProp({ key, value })`.
    adminInvoicesSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"].includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearAdminInvoicesFilters(state) {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- filter options ---------------- */
      .addCase(getAdminInvoicesFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getAdminInvoicesFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getAdminInvoicesFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- invoices list ---------------- */
      .addCase(getAdminInvoices.pending, (state) => {
        state.fetching = true
      })
      .addCase(getAdminInvoices.fulfilled, (state, action) => {
        state.fetching = false
        const result = action.payload
        state.result = result.results ? result : { count: result?.length || 0, results: result }
      })
      .addCase(getAdminInvoices.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- update status ---------------- */
      .addCase(updateAdminInvoice.pending, (state) => {
        state.fetching = true
      })
      .addCase(updateAdminInvoice.fulfilled, (state, action) => {
        state.fetching = false
        applyInvoiceUpdate(state, action.payload)
      })
      .addCase(updateAdminInvoice.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- confirm invoice ---------------- */
      .addCase(confirmInvoice.pending, (state) => {
        state.fetching = true
      })
      .addCase(confirmInvoice.fulfilled, (state, action) => {
        state.fetching = false
        applyInvoiceUpdate(state, action.payload)
      })
      .addCase(confirmInvoice.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- change invoice amount ---------------- */
      .addCase(changeInvoiceAmount.pending, (state) => {
        state.fetching = true
      })
      .addCase(changeInvoiceAmount.fulfilled, (state, action) => {
        state.fetching = false
        applyInvoiceUpdate(state, action.payload)
      })
      .addCase(changeInvoiceAmount.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- export ---------------- */
      .addCase(getAdminInvoicesExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getAdminInvoicesExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getAdminInvoicesExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, (state) => ({
        ...initialState,
        filters: state.filters,
        columnsAdmin: state.columnsAdmin,
        visibleFiltersAdmin: state.visibleFiltersAdmin
      }))

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["admin-invoices"]?.data
        if (!preference || preference.version !== ADMIN_INVOICES_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const adminInvoicesActions = slice.actions

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.adminInvoices.filters, initialState.filters)

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const ADMIN_INVOICES_PREFERENCES_VERSION = 1

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "adminInvoices",
    storage,
    // Mirrors what the legacy `logout` reducer case preserved — the users list, filter options,
    // and export result are all re-fetched or transient.
    whitelist: ["filters", "columnsAdmin", "visibleFiltersAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
